import { safeFetchAPI, SafeFetchResponse } from '@/api';
import { log, utils } from '@/helpers';
import { Setter } from '@/legacy-types';
import {
	Campaign,
	CampaignsV2Response,
	CoMarketingEditEventData,
	CoMarketingEditRequest,
	CoMarketingEditRequestReq,
	CoMarketingEventReq,
	GetMostRecentCoMarketingEditReqFilters,
	InstalledCoMarketingTemplate,
} from '@/types';
import { cloneDeep, isEqual } from 'lodash';
import { create } from 'zustand';
import { useCoMarketingStore } from './useCoMarketingStore';
import { CampaignEditingMethod } from '@/pages/campaign-builder/campaign.builder.utils';
import { useCampaignBuilderStore } from './useCampaignBuilderStore';
import { useCoMarketingChangesPromptStore } from '@/pages/campaign-builder/components/CoMarketingChangesPrompt';

export type CoMarketingSponsorEditStoreState = {
	loaded: boolean;
	loading: boolean;
	sponsorRequest: Partial<CoMarketingEditRequest> | undefined;
	eventData: CoMarketingEditEventData | undefined;
	ogEventData: CoMarketingEditEventData | undefined;
};

interface CoMarketingSponsorEditStoreActions {
	init: (resp: CampaignsV2Response) => Promise<void>;
	initFromEditRequest: (editRequest: CoMarketingEditRequest) => void;
	setEventData: (eventData: Setter<Partial<CoMarketingEditEventData | undefined>>) => void;
	saveEditRequest: (comment: string) => Promise<void>;
	cancelEditRequest: (comment?: string) => Promise<boolean>;
	denyEdits: (comment?: string) => Promise<boolean>;
	approveEdits: (comment?: string) => Promise<boolean>;
	reset: (loaded?: boolean) => void;
}

export type CoMarketingSponsorEditStore = CoMarketingSponsorEditStoreState & CoMarketingSponsorEditStoreActions;

const initialState: CoMarketingSponsorEditStoreState = {
	loaded: false,
	loading: false,
	sponsorRequest: undefined,
	eventData: undefined,
	ogEventData: undefined,
};

export const useCoMarketingSponsorEditStore = create<CoMarketingSponsorEditStore>((set, get) => ({
	...initialState,

	initFromEditRequest: (editRequest) => {
		set({
			loaded: true,
			sponsorRequest: editRequest,
			eventData: editRequest.eventData,
			ogEventData: cloneDeep(editRequest.eventData),
		});
	},

	// Check for any pending edits by the sponsor to the template
	init: async (resp: CampaignsV2Response) => {
		const { installedTemplate: installedCoMarketing } = resp;
		const { id: templateID, installedFromUserID: sponsorID } = installedCoMarketing ?? {};

		set({ loading: true });

		const [editRequest, error] = await safeFetchAPI<CoMarketingEditRequest>(`/co-marketing/installed/edit/:uid/${templateID}`, {
			method: 'POST',
			payload: {
				templateID,
				approvalStatus: 'pending',
				triggerUID: sponsorID,
			} as GetMostRecentCoMarketingEditReqFilters,
		});

		if (error) {
			return;
		}

		const eventData = editRequest?.eventData;
		const ogEventData = cloneDeep(eventData);
		set({
			loaded: true,
			sponsorRequest: editRequest,
			eventData,
			ogEventData,
			loading: false,
		});

		// If the edit request has a campaign and user is the sponsor
		// We instantly apply it to the campaign builder store
		if ((editRequest?.eventData?.campaign || editRequest?.eventData?.budget) && sponsorID === utils.uid) {
			useCampaignBuilderStore.getState().applyEditRequestCampaign(CampaignEditingMethod.SPONSOR_EDITING_INSTALLED);
		}

		// If there is a sponsor edit request & the installer is viewing
		// We open the co-marketing changes prompt
		if ((editRequest?.eventData?.campaign || editRequest?.eventData?.budget) && sponsorID !== utils.uid) {
			useCoMarketingChangesPromptStore.getState().setOpen('sponsor');
		}

		log('init sponsor edit request', editRequest);
	},

	setEventData: (eventData) => {
		const currentEventData = get().eventData;
		const nextEventData = typeof eventData === 'function' ? eventData(currentEventData) : eventData;
		set({ eventData: nextEventData });
	},

	saveEditRequest: async (comment: string) => {
		const editRequest = get().sponsorRequest;
		if (!editRequest) {
			return;
		}

		const installedCoMarketingTemplate = useCoMarketingStore.getState().installedTemplate;
		if (!installedCoMarketingTemplate) {
			utils?.toaster?.addToast?.({
				message: 'No installed co-marketing template found',
				type: 'danger',
				duration: 10000,
			});
			return;
		}

		const { eventData } = get();
		const { id: templateID, campaignID: installerID } = installedCoMarketingTemplate;

		set({ loading: true });

		let response: SafeFetchResponse<CoMarketingEditRequest> | undefined;

		// If the edit request has an ID, we are updating
		if (editRequest.id) {
			// If no changes to event data throw an error
			if (isEqual(eventData, get().ogEventData)) {
				utils?.toaster?.addToast?.({
					message: 'No changes to event data',
					type: 'danger',
					duration: 10000,
				});
				return;
			}
			response = await safeFetchAPI<CoMarketingEditRequest>(`/co-marketing/installed/edit/:uid/${editRequest.id}`, {
				method: 'PUT',
				payload: {
					notify: true,
					installerID,
					templateID,
					comment,
					eventData,
				} as CoMarketingEditRequestReq,
			});
		} else {
			// Create new edit request
			response = await safeFetchAPI<CoMarketingEditRequest>('/co-marketing/installed/edit/:uid', {
				method: 'POST',
				payload: {
					notify: true,
					installerID,
					templateID,
					comment,
					eventData,
				} as CoMarketingEditRequestReq,
			});
		}

		const [data, error] = response;

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Failed to save edit request',
				type: 'danger',
				duration: 10000,
			});
			set({ loading: false });
			return;
		}

		set({ sponsorRequest: data, loading: false });
	},

	cancelEditRequest: async (comment?: string) => {
		const editRequest = get().sponsorRequest;
		if (!editRequest) {
			return false;
		}

		const { id: templateID, userID: installerID } = useCoMarketingStore.getState().installedTemplate ?? {};
		if (!templateID || !installerID) {
			return false;
		}

		const payload: CoMarketingEventReq = {
			installerID,
			templateID,
			notify: true,
			comment,
		};

		const [_, error] = await safeFetchAPI(`/co-marketing/installed/edit/:uid/${editRequest.id}/cancel`, { method: 'PUT', payload });
		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to cancel edit request',
			});
			return false;
		}

		utils?.toaster?.addToast?.({
			type: 'success',
			message: 'Edit request cancelled',
		});

		get().reset(true);

		return true;
	},

	denyEdits: async (comment?: string) => {
		const editRequest = get().sponsorRequest;
		if (!editRequest) {
			return false;
		}

		const { id: templateID, userID: installerID } = useCoMarketingStore.getState().installedTemplate ?? {};
		if (!templateID || !installerID) {
			return false;
		}

		const payload: CoMarketingEventReq = {
			installerID,
			templateID,
			notify: true,
			comment,
		};

		const [_, error] = await safeFetchAPI(`/co-marketing/installed/edit/:uid/${editRequest.id}/deny`, { method: 'PUT', payload });
		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to deny edit request',
			});
			return false;
		}

		utils?.toaster?.addToast?.({
			type: 'success',
			message: 'Edit request denied',
		});

		return true;
	},

	approveEdits: async (comment?: string) => {
		const editRequest = get().sponsorRequest;
		if (!editRequest) {
			return false;
		}

		const { id: templateID, userID: installerID } = useCoMarketingStore.getState().installedTemplate ?? {};
		if (!templateID || !installerID) {
			return false;
		}

		const payload: CoMarketingEventReq = {
			installerID,
			templateID,
			notify: true,
			comment,
		};

		const [_, error] = await safeFetchAPI(`/co-marketing/installed/edit/:uid/${editRequest.id}/approve`, { method: 'PUT', payload });
		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to approve edit request',
			});
			return false;
		}

		utils?.toaster?.addToast?.({
			type: 'success',
			message: 'Edit request approved',
		});

		return true;
	},

	reset: (loaded?: boolean) => set({ ...initialState, loaded }),
}));
